import { useCallback } from 'react';

import { useI18n } from '@change-corgi/core/react/i18n';
import { useNavigate } from '@change-corgi/core/react/router';
import { useTracking } from '@change-corgi/core/react/tracking';

import { useLoginRedirectUrl } from 'src/app/shared/hooks/auth';
import { useLoginState } from 'src/app/shared/hooks/session';

import type { PartialComment } from './types';
import { useCommentDeleteModal } from './useCommentDeleteModal';

export type { PartialComment };

type Result = ModelHookResult<
	{
		a11yLabel: string;
	},
	{
		deleteComment: () => Promise<void>;
	}
>;

export type Options = {
	comment: PartialComment;
	onBeforeDelete?: () => void;
	onDeleted?: () => void;
};

export function useCommentDelete({ comment, onBeforeDelete, onDeleted }: Options): Result {
	const loginState = useLoginState();
	const trackDeleted = useTrackDeleted(comment);
	const navigateToLogin = useNavigateToLogin();
	const openDeleteModal = useCommentDeleteModal();

	return {
		data: {
			a11yLabel: useA11yLabel(comment),
		},
		actions: {
			deleteComment: useCallback(async () => {
				if (loginState !== 'AUTHENTICATED') {
					navigateToLogin();
					return;
				}

				onBeforeDelete?.();

				const result = await openDeleteModal({ comment }).waitForResult();
				// only tracking likes
				if (result?.success) {
					trackDeleted();
					onDeleted?.();
				}
			}, [loginState, navigateToLogin, comment, trackDeleted, openDeleteModal, onBeforeDelete, onDeleted]),
		},
	};
}

function useA11yLabel(comment: PartialComment) {
	const { translate } = useI18n();

	return translate('corgi.petition.details.comments.actions.delete_a11y', {
		author: comment.user.displayName,
		date: comment.createdAtLocalized,
	});
}
function useTrackDeleted(comment: PartialComment) {
	const track = useTracking();

	return useCallback(() => {
		void track('comment_delete', {
			likes: comment.likes,
			role: comment.role.toLowerCase(),
			comment_id: `comment_${comment.id}`,
			parent_id: comment.parentId || undefined,
		});
	}, [track, comment]);
}

function useNavigateToLogin() {
	const navigate = useNavigate();
	const loginPath = useLoginRedirectUrl();

	return useCallback(() => navigate(loginPath), [navigate, loginPath]);
}
